import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '@env/environment';

export interface ImportRowResult {
  action: 'created' | 'updated' | 'unchanged' | 'skipped' | 'error' | 'conflict' | 'archived';
  key: any;
  message?: string;
  reason?: string;
  warnings?: string[];
  [extra: string]: any;
}

export interface ImportResult {
  type: string;
  filename: string | null;
  dry_run: boolean;
  totals: {
    total: number;
    created: number;
    updated: number;
    unchanged?: number;
    skipped: number;
    errors: number;
    conflicts?: number;
    archived?: number;
    review_conflicts?: number;
  };
  reasons?: { [reason: string]: number };
  unmatched_technicians: { [name: string]: number };
  unknown_intervals: { [text: string]: number };
  created_technicians: { [label: string]: number };
  messages: string[];
  rows: ImportRowResult[];
}

export type ImportTarget = 'artikel' | 'clients' | 'kunden-master';

export interface ImportOptions {
  artTyp?: string;
  /** kunden-master only */
  createMissing?: boolean;
  /** kunden-master only: archive customers missing from the export (5 % guard) */
  archiveMissing?: boolean;
  /** kunden-master only: bypass the 5 % archive guard */
  forceArchive?: boolean;
}

export type ConflictStatus = 'pending' | 'accepted' | 'rejected' | 'superseded';

export interface ImportConflict {
  id: string;
  import_run_id: string | null;
  source_type: string;
  conflict_type: string;
  entity_type: string;
  entity_id: string;
  entity_label: string | null;
  field: string;
  current_value: string | null;
  proposed_value: string | null;
  source_label: string | null;
  source_keys: string[];
  occurrences: number;
  status: ConflictStatus;
  resolved_by: string | null;
  resolved_at: string | null;
  created_at: string;
  updated_at: string;
  group_key: string;
  competing: number;
}

export interface ConflictFilter {
  status?: ConflictStatus | 'all';
  entity_type?: string;
  field?: string;
  conflict_type?: string;
  q?: string;
}

export interface ConflictPage {
  items: ImportConflict[];
  total: number;
  page: number;
  per_page: number;
  facets: { [facet: string]: { [value: string]: number } };
}

export interface ConflictResolveSummary {
  accepted: number;
  rejected: number;
  superseded: number;
  ambiguous: number;
  failed: number;
  errors: string[];
}

export interface SourceRecordVersion {
  id: string;
  version: number;
  row_hash: string;
  raw: { [column: string]: string };
  import_run_id: string | null;
  filename: string | null;
  row_number: number | null;
  first_seen_at: string;
  last_seen_at: string;
}

export interface SourceRecord {
  source_type: string;
  source_key: string;
  versions: SourceRecordVersion[];
}

/**
 * ImportSyncService
 *
 * Talks to the admin-only /v5 import endpoints. Rows are parsed client-side
 * (CSV/XLSX) and sent as normalized objects keyed by their source column header.
 */
@Injectable({ providedIn: 'root' })
export class ImportSyncService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  preview(target: ImportTarget, rows: any[], filename: string, options: ImportOptions = {}): Observable<ImportResult> {
    return this.post(`${this.apiUrl}/import/${target}/preview`, target, rows, filename, options);
  }

  sync(target: ImportTarget, rows: any[], filename: string, options: ImportOptions = {}): Observable<ImportResult> {
    return this.post(`${this.apiUrl}/import/${target}/sync`, target, rows, filename, options);
  }

  listConflicts(filter: ConflictFilter, page = 1, perPage = 50): Observable<ConflictPage> {
    let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
    Object.entries(filter).forEach(([key, value]) => {
      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params = params.set(key, String(value));
      }
    });
    return this.unwrap(this.http.get<any>(`${this.apiUrl}/import/conflicts`, { params }));
  }

  /** Resolve the given ids, or every pending conflict matching `allMatchingFilter`. */
  resolveConflicts(
    decision: 'accept' | 'reject',
    ids: string[],
    allMatchingFilter?: ConflictFilter
  ): Observable<ConflictResolveSummary> {
    const body: any = { decision };
    if (allMatchingFilter) {
      body.all_matching_filter = allMatchingFilter;
    } else {
      body.ids = ids;
    }
    return this.unwrap(this.http.post<any>(`${this.apiUrl}/import/conflicts/resolve`, body));
  }

  /** Original BMD rows linked to an entity. Superuser only (the API answers 403 otherwise). */
  sourceRecords(entityType: 'kunde' | 'standort' | 'artikel', entityId: string | number): Observable<SourceRecord[]> {
    const params = new HttpParams().set('entity_type', entityType).set('entity_id', String(entityId));
    return this.unwrap(this.http.get<any>(`${this.apiUrl}/import/source-records`, { params }));
  }

  private post(url: string, target: ImportTarget, rows: any[], filename: string, options: ImportOptions): Observable<ImportResult> {
    const payload: any = { rows, filename, requested_by: 'webapp' };
    if (options.artTyp) {
      payload.art_typ = options.artTyp;
    }
    if (target === 'kunden-master') {
      payload.create_missing = options.createMissing !== false;
      payload.archive_missing = options.archiveMissing !== false;
      payload.force_archive = options.forceArchive === true;
    }
    return this.unwrap(this.http.post<any>(url, payload));
  }

  private unwrap<T>(request: Observable<{ result: number; data?: T; error?: string }>): Observable<T> {
    return request.pipe(
      map((response) => {
        if (response.result !== 1 || response.data === undefined) {
          throw new Error(response.error || 'Import fehlgeschlagen');
        }
        return response.data;
      }),
      catchError(this.handleError)
    );
  }

  private handleError(error: HttpErrorResponse | Error) {
    if (!(error instanceof HttpErrorResponse)) {
      return throwError(() => error);
    }
    const body = typeof error.error === 'string' ? error.error : '';
    if (body.includes('Diese Seite existiert nicht') || /<!doctype html>/i.test(body)) {
      return throwError(
        () =>
          new Error(
            'Der Import-Endpunkt ist auf dem API-Server nicht verfügbar. ' +
              'Bitte die aktuelle API-Version deployen (POST /v5/import/kunden-master/preview).'
          )
      );
    }

    const message = error.error?.error || error.message || 'Unbekannter Fehler beim Import';
    return throwError(() => new Error(message));
  }
}
